import OptionallyVisible from 'components/optionallyVisible';

import { APIError } from 'types/error';

import { KEYS } from '../../keys';
import { OrderPwcData } from '../../types';
import { ChargeExchangeDetailsSection } from '../chargeExchangeDetails/chargeExchangeDetails';
import { PaymentSection } from '../payment/payment';
import { PayoutSection } from '../payout/payout';
import { RefundSection } from '../refund/refund';
import { RefundExchangeDetailsSection } from '../refundExchangeDetails/refundExchangeDetails';
import { RefundSummarySection } from '../refundSummary/refundSummary';
import { SettlementDetailsSection } from '../settlementDetails/settlementDetails';
import { FormattedTransaction } from '../transactionsList/formatTransaction';
import { Container, SubContainer } from './styles';

export interface TransactionDetailsProps {
  orderPwcData: OrderPwcData;
  shouldShowExchangeDetailsSection: boolean;
  shouldShowSettlementDetailsSection: boolean;
  rowId: string | number;
  isS4cUser: boolean;
  transactions: FormattedTransaction[];
  cancelRefund: {
    trigger: (orderUuid: string, refundUuid: string) => Promise<{ error?: APIError }>;
    isLoading: boolean;
  };
}

export const TransactionDetails = ({
  orderPwcData,
  shouldShowExchangeDetailsSection,
  shouldShowSettlementDetailsSection,
  rowId,
  isS4cUser,
  transactions,
  cancelRefund,
}: TransactionDetailsProps) => {
  const sectionsProps = {
    orderData: orderPwcData,
    rowId,
    isS4cUser,
  };

  const transaction = transactions.find((tx) => tx.uuid === rowId);
  if (!transaction) {
    return null;
  }

  const renderChargeDetails = () => {
    const shouldShowPayoutSection = Boolean(orderPwcData?.charges.find((charge) => charge.uuid === rowId)?.payout?.uuid);

    return (
      <Container>
        <SubContainer>
          <PaymentSection {...sectionsProps} />
          <OptionallyVisible visible={shouldShowSettlementDetailsSection}>
            <SettlementDetailsSection {...sectionsProps} />
          </OptionallyVisible>
        </SubContainer>

        <SubContainer>
          <ChargeExchangeDetailsSection {...sectionsProps} />
          <OptionallyVisible visible={shouldShowPayoutSection}>
            <PayoutSection {...sectionsProps} />
          </OptionallyVisible>
        </SubContainer>
      </Container>
    );
  };

  const renderRefundDetails = () => {
    const refund = orderPwcData?.refunds.find((refund) => refund.uuid === rowId);
    const hasRefundItems = Boolean(refund?.items?.length);
    const hasRedirectUrl = Boolean(refund?.redirectUrl);
    const hasCancelRedirectUrl = Boolean(refund?.cancelRedirectUrl);

    const shouldShowRefundDetailsSection = hasRefundItems || hasRedirectUrl || hasCancelRedirectUrl;

    return (
      <Container>
        <SubContainer>
          <RefundSection {...sectionsProps} cancelRefund={cancelRefund} />
        </SubContainer>

        <SubContainer>
          <OptionallyVisible visible={shouldShowRefundDetailsSection}>
            <RefundSummarySection {...sectionsProps} />
          </OptionallyVisible>
          <RefundExchangeDetailsSection {...sectionsProps} />
        </SubContainer>
      </Container>
    );
  };

  const renderers: Record<string, () => JSX.Element> = {
    [KEYS.CHARGE_TYPE]: renderChargeDetails,
    [KEYS.REFUND_TYPE]: renderRefundDetails,
  };

  const render = renderers[transaction.type];
  return render ? render() : null;
};
